/**
 * The list of threads, and the way between them.
 *
 * Each conversation carries its own memory — the model is given the turns of the thread it
 * is in and no others — so this list is not cosmetic: it is how someone returns to a
 * context they already built, instead of re-establishing which repository they meant.
 *
 * A thread names itself from its first message. That name is a guess at what the thread is
 * about, so it can be corrected; the rename is inline, because a dialog for editing one
 * line of text is a heavier thing than the edit.
 */
import { useEffect, useRef, useState } from 'react'
import { MessageSquare, Pencil, Plus, Trash2 } from 'lucide-react'

import { useConversations, useDeleteConversation, useRenameConversation } from '@/features/chat/useConversations'
import { cn } from '@/lib/utils/cn'
import { formatRelative } from '@/lib/utils/format'

import type { ConversationSummary } from '@/lib/api/chat'

export function ConversationRail({
  currentId,
  onOpen,
  onNew,
  disabled = false,
  className,
}: {
  currentId: string | null
  onOpen: (id: string) => void
  onNew: () => void
  disabled?: boolean
  className?: string
}) {
  const { data, isLoading } = useConversations()
  const rename = useRenameConversation()
  const remove = useDeleteConversation()
  const [editing, setEditing] = useState<string | null>(null)
  const [confirming, setConfirming] = useState<string | null>(null)

  const conversations = data?.conversations ?? []

  return (
    <aside
      // `flex-1` in both placements: the column beside the chat and the drawer over it are
      // each a flex parent, and a rail that stops at its last row leaves the New button
      // floating in the middle of the screen.
      className={cn('flex min-h-0 flex-1 flex-col bg-surface', className)}
      aria-label="Conversations"
    >
      <header className="flex h-14 shrink-0 items-center justify-between gap-2 px-4">
        <h2 className="text-lg font-semibold text-ink">Conversations</h2>
        <button
          type="button"
          onClick={onNew}
          disabled={disabled}
          title="Start a thread with its own memory"
          className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-ground
            transition-colors hover:bg-primary-bright disabled:cursor-not-allowed
            disabled:bg-raised disabled:text-ink-faint"
        >
          <Plus aria-hidden="true" className="h-[18px] w-[18px]" />
          <span className="sr-only">New</span>
        </button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading ? (
          <p className="px-4 py-3 text-xs text-ink-muted">Loading…</p>
        ) : conversations.length === 0 ? (
          <p className="px-4 py-3 text-xs text-ink-muted">
            No conversations yet. The first message names this one.
          </p>
        ) : (
          <ul className="space-y-0.5 px-2 pb-2">
            {conversations.map((conversation) => (
              <Row
                key={conversation.conversation_id}
                conversation={conversation}
                active={conversation.conversation_id === currentId}
                editing={editing === conversation.conversation_id}
                confirming={confirming === conversation.conversation_id}
                disabled={disabled}
                onOpen={() => onOpen(conversation.conversation_id)}
                onStartRename={() => {
                  setConfirming(null)
                  setEditing(conversation.conversation_id)
                }}
                onRename={(title) => {
                  setEditing(null)
                  if (title && title !== conversation.title) {
                    rename.mutate({ id: conversation.conversation_id, title })
                  }
                }}
                onAskDelete={() => {
                  setEditing(null)
                  setConfirming(conversation.conversation_id)
                }}
                onCancelDelete={() => setConfirming(null)}
                onDelete={() => {
                  setConfirming(null)
                  remove.mutate(conversation.conversation_id, {
                    // Deleting the thread on screen would leave the page showing turns that
                    // no longer exist, so it starts a fresh one — the same as New.
                    onSuccess: () => {
                      if (conversation.conversation_id === currentId) onNew()
                    },
                  })
                }}
              />
            ))}
          </ul>
        )}
      </div>
    </aside>
  )
}

function Row({
  conversation,
  active,
  editing,
  confirming,
  disabled,
  onOpen,
  onStartRename,
  onRename,
  onAskDelete,
  onCancelDelete,
  onDelete,
}: {
  conversation: ConversationSummary
  active: boolean
  editing: boolean
  confirming: boolean
  disabled: boolean
  onOpen: () => void
  onStartRename: () => void
  onRename: (title: string) => void
  onAskDelete: () => void
  onCancelDelete: () => void
  onDelete: () => void
}) {
  const [draft, setDraft] = useState(conversation.title)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setDraft(conversation.title)
  }, [conversation.title])

  useEffect(() => {
    if (editing) inputRef.current?.select()
  }, [editing])

  if (editing) {
    return (
      <li className="px-1 py-1">
        <form
          onSubmit={(event) => {
            event.preventDefault()
            onRename(draft.trim())
          }}
        >
          <input
            ref={inputRef}
            value={draft}
            aria-label="Conversation name"
            onChange={(event) => setDraft(event.target.value)}
            onBlur={() => onRename(draft.trim())}
            onKeyDown={(event) => {
              if (event.key === 'Escape') onRename('')
            }}
            className="field py-1.5 text-xs"
          />
        </form>
      </li>
    )
  }

  return (
    <li
      className={cn(
        'group rounded-lg transition-colors duration-200 ease-in-out',
        active ? 'bg-raised' : 'hover:bg-hover/60',
      )}
    >
      <div className="flex min-h-[76px] items-center gap-3 px-2.5 py-2">
        {/* The round mark a chat list opens each row with, drawn from the thread's own
            first letter rather than fetched from anywhere. */}
        <span
          aria-hidden="true"
          className={cn(
            'flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-semibold',
            active ? 'border border-line bg-active text-ink' : 'bg-overlay text-ink-muted',
          )}
        >
          <MessageSquare className="h-5 w-5" />
        </span>
        <button
          type="button"
          onClick={onOpen}
          disabled={disabled}
          aria-current={active ? 'true' : undefined}
          className="min-w-0 flex-1 self-stretch py-1 text-left disabled:cursor-not-allowed"
        >
          <span
            className={cn(
              'block truncate text-base',
              active ? 'font-medium text-ink' : 'text-ink-dim',
            )}
          >
            {conversation.title}
          </span>
          <span className="mt-0.5 block truncate text-xs text-ink-faint">
            {formatRelative(conversation.updated_at)} · {conversation.turn_count}{' '}
            {conversation.turn_count === 1 ? 'message' : 'messages'}
          </span>
        </button>

        {confirming ? (
          <span className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={onDelete}
              className="rounded-md bg-fail px-2 py-1 text-micro font-semibold text-ground
                hover:bg-fail/90"
            >
              Delete
            </button>
            <button
              type="button"
              onClick={onCancelDelete}
              className="rounded-md px-2 py-1 text-micro font-medium text-ink-muted hover:bg-surface
                hover:text-ink"
            >
              Keep
            </button>
          </span>
        ) : (
          // Visible on hover and on keyboard focus. Focus matters as much as hover here:
          // controls that only appear under a pointer do not exist for a keyboard.
          <span
            className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity
              focus-within:opacity-100 group-hover:opacity-100"
          >
            <button
              type="button"
              onClick={onStartRename}
              title="Rename"
              aria-label={`Rename ${conversation.title}`}
              className="flex h-6 w-6 items-center justify-center rounded text-ink-muted
                hover:bg-surface hover:text-ink"
            >
              <Pencil aria-hidden="true" className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={onAskDelete}
              title="Delete"
              aria-label={`Delete ${conversation.title}`}
              className="flex h-6 w-6 items-center justify-center rounded text-ink-muted
                hover:bg-surface hover:text-fail"
            >
              <Trash2 aria-hidden="true" className="h-3.5 w-3.5" />
            </button>
          </span>
        )}
      </div>
    </li>
  )
}
